import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Card } from "../components/ui/card";
import { AlertCircle, Smartphone } from "lucide-react";
import { Wordmark } from "../components/Wordmark";
import { toast } from "sonner";

export default function Login() {
  const { fetchMe, setActiveCompany } = useAuth();
  const [choices, setChoices] = useState([]);
  const navigate = useNavigate();
  const [step, setStep] = useState("creds");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [mfa, setMfa] = useState(null);
  const [remember, setRemember] = useState(true);
  const [showSecret, setShowSecret] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const finishLogin = async () => {
    const me = await fetchMe();
    const ms = me?.memberships || [];
    if (ms.length > 1) { localStorage.removeItem("activeCompanyId"); setChoices(ms); setStep("choose"); return; }
    if (ms[0]) setActiveCompany(ms[0].company_id);
    navigate("/app");
  };

  const submitCreds = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.requires_mfa === false) { toast.success("Signed in"); await finishLogin(); return; }
      setMfa(data);
      setStep(data.requires_setup ? "setup" : "mfa");
    } catch (e2) { setErr(apiError(e2)); }
    setBusy(false);
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post("/auth/verify-mfa", { mfa_token: mfa.mfa_token, code, remember_device: remember });
      toast.success(step === "setup" ? "Authenticator set up — signed in" : "Signed in");
      await finishLogin();
    } catch (e2) { setErr(apiError(e2)); }
    setBusy(false);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md rounded-3xl p-8">
        <div className="mb-6"><Wordmark className="text-2xl" /></div>

        {err && (
          <div data-testid="login-error" className="mb-4 flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> <span>{err}</span>
          </div>
        )}

        {step === "choose" && (
          <div className="space-y-3" data-testid="choose-agency">
            <h1 className="font-head text-2xl font-bold tracking-tight">Choose your agency</h1>
            <p className="text-sm text-muted-foreground">Your email is linked to more than one agency. You work inside one agency per session — sign out to use another.</p>
            {choices.map((m) => <button key={m.company_id} onClick={() => { setActiveCompany(m.company_id); navigate("/app"); }} className="flex w-full items-center justify-between rounded-xl border border-border px-4 py-3 text-left hover:border-primary hover:bg-primary/5" data-testid={`choose-agency-${m.company_id}`}><span><span className="block font-semibold">{m.company_name}</span><span className="text-xs capitalize text-muted-foreground">{m.role.replace(/_/g, " ")}</span></span><span className="text-primary">→</span></button>)}
          </div>
        )}

        {step === "creds" && (
          <form onSubmit={submitCreds} className="space-y-4">
            <h1 className="font-head text-2xl font-bold tracking-tight">Agency sign in</h1>
            <p className="text-sm text-muted-foreground">Password plus Google Authenticator.</p>
            <div className="space-y-2">
              <Label htmlFor="email">Work email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" className="h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" className="h-11" />
            </div>
            <Button type="submit" className="h-11 w-full rounded-full" disabled={busy} data-testid="login-submit">{busy ? "Checking…" : "Continue"}</Button>
            <Link to="/forgot-password" className="block text-center text-sm font-semibold text-primary hover:underline" data-testid="forgot-link">Forgot password?</Link>
          </form>
        )}

        {step !== "creds" && (
          <form onSubmit={submitMfa} className="space-y-4">
            {step === "setup" ? (
              <>
                <h1 className="font-head text-2xl font-bold tracking-tight">Set up your authenticator</h1>
                <p className="text-sm text-muted-foreground">One-time setup. Open <b>Google Authenticator</b> (or Authy / Microsoft Authenticator), tap <b>+</b>, and scan this code.</p>
                <div className="flex justify-center rounded-2xl border border-border bg-white p-3">
                  <img src={mfa.qr_data_url} alt="Authenticator QR code" className="h-48 w-48" data-testid="totp-qr" />
                </div>
                <button type="button" onClick={() => setShowSecret((v) => !v)} className="w-full text-xs text-muted-foreground underline" data-testid="totp-show-secret">
                  {showSecret ? "Hide" : "Can't scan? Enter the key manually"}
                </button>
                {showSecret && <p className="break-all rounded-xl bg-muted p-3 text-center font-mono text-sm" data-testid="totp-secret">{mfa.secret}</p>}
              </>
            ) : (
              <>
                <h1 className="font-head text-2xl font-bold tracking-tight">Enter your code</h1>
                <p className="flex items-center gap-2 text-sm text-muted-foreground"><Smartphone className="h-4 w-4" /> Open Google Authenticator and enter the 6-digit code for Airfistuus.</p>
                <Link to="/reset-authenticator" className="block text-sm font-semibold text-primary hover:underline" data-testid="lost-authenticator-link">Lost your authenticator or codes rejected? Reset it</Link>
              </>
            )}
            <div className="space-y-2">
              <Label htmlFor="code">{step === "setup" ? "Code shown in the app" : "Verification code"}</Label>
              <Input id="code" data-testid="mfa-code" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} required className="h-12 text-center font-mono text-xl tracking-[0.4em]" maxLength={6} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="remember-device" />
              Remember this device for 30 days
            </label>
            <Button type="submit" className="h-11 w-full rounded-full" disabled={busy || code.length < 6} data-testid="mfa-submit">
              {busy ? "Verifying…" : step === "setup" ? "Confirm & sign in" : "Verify & sign in"}
            </Button>
            <button type="button" onClick={() => { setStep("creds"); setCode(""); }} className="w-full text-sm text-muted-foreground hover:text-foreground">Back</button>
          </form>
        )}
      </Card>
    </div>
  );
}
